import { useState, useMemo } from "react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area
} from "recharts";
import { Download, ChevronDown, TrendingUp, Clock, Target, Zap, FileText, FileSpreadsheet, File } from "lucide-react";

const API = '/api';

const STATUS_COLORS = {
  sent: "#10B981",
  failed: "#EF4444",
  skipped_no_rcs: "#F97316",
  skipped: "#F59E0B",
  error: "#F43F5E",
  blocked_sms: "#FB7185",
  pending: "#FBBF24",
  queued: "#22D3EE",
};

const STATUS_LABELS = {
  sent: "Enviados",
  failed: "Fallidos",
  skipped_no_rcs: "Sin RCS",
  skipped: "Saltados",
  pending: "Pendientes",
  queued: "En Cola",
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs text-zinc-300 font-medium">{d.name || d.payload?.name || d.payload?.hour}</p>
      <p className="text-sm font-bold" style={{ color: d.color || d.fill || '#fff' }}>
        {d.value}
      </p>
    </div>
  );
};

export default function CampaignAnalytics({ campaignId, campaignName, stats, logs }) {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportFilter, setExportFilter] = useState("all");

  // --- Donut chart data ---
  const donutData = useMemo(() => {
    if (!stats) return [];
    const sent = stats.sent || 0;
    const sms = (stats.skipped_no_rcs || 0) + (stats.skipped || 0);
    const errors = (stats.failed || 0) + (stats.error || 0) + (stats.blocked_sms || 0);
    const pending = (stats.pending || 0) + (stats.queued || 0);
    const entries = [
      { name: "RCS", value: sent, color: "#10B981" },
      { name: "SMS", value: sms, color: "#F97316" },
      { name: "Error", value: errors, color: "#EF4444" },
      { name: "Pendientes", value: pending, color: "#FBBF24" },
    ];
    return entries.filter(e => e.value > 0);
  }, [stats]);

  // --- Hourly distribution from logs ---
  const hourlyData = useMemo(() => {
    if (!logs || logs.length === 0) return [];
    const hours = {};
    for (let h = 0; h < 24; h++) hours[h] = { hour: `${h.toString().padStart(2, '0')}:00`, sent: 0, failed: 0, skipped: 0 };
    
    logs.forEach(log => {
      if (!log.timestamp) return;
      const h = new Date(log.timestamp).getHours();
      if (log.status === 'sent') hours[h].sent++;
      else if (log.status === 'failed') hours[h].failed++;
      else hours[h].skipped++;
    });

    return Object.values(hours).filter(h => h.sent > 0 || h.failed > 0 || h.skipped > 0);
  }, [logs]);

  // --- Timeline (cumulative over time) ---
  const timelineData = useMemo(() => {
    if (!logs || logs.length === 0) return [];
    const sorted = [...logs].filter(l => l.timestamp).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    let cumSent = 0, cumFailed = 0, cumSkipped = 0;
    const points = [];
    let lastTime = null;
    
    sorted.forEach((log, i) => {
      if (log.status === 'sent') cumSent++;
      else if (log.status === 'failed') cumFailed++;
      else cumSkipped++;
      
      const time = new Date(log.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
      // Group by minute to avoid too many points
      if (time !== lastTime || i === sorted.length - 1) {
        points.push({ time, sent: cumSent, failed: cumFailed, skipped: cumSkipped, total: cumSent + cumFailed + cumSkipped });
        lastTime = time;
      }
    });
    return points;
  }, [logs]);

  // --- KPIs ---
  const kpis = useMemo(() => {
    if (!stats || !logs) return {};
    const total = stats.total || 0;
    const sent = stats.sent || 0;
    const failed = stats.failed || 0;
    const errors = (stats.error || 0) + (stats.blocked_sms || 0);
    const skippedNoRcs = (stats.skipped_no_rcs || 0) + (stats.skipped || 0);
    const processed = sent + failed + skippedNoRcs + errors;
    
    const successRate = processed > 0 ? Math.round((sent / processed) * 100) : 0;
    const rcsRate = total > 0 ? Math.round(((total - skippedNoRcs) / total) * 100) : 0;
    
    // Average time between sends
    const sentLogs = logs.filter(l => l.status === 'sent' && l.timestamp).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    let avgDelay = 0;
    if (sentLogs.length > 1) {
      let totalDelay = 0;
      for (let i = 1; i < sentLogs.length; i++) {
        totalDelay += (new Date(sentLogs[i].timestamp) - new Date(sentLogs[i-1].timestamp)) / 1000;
      }
      avgDelay = Math.round(totalDelay / (sentLogs.length - 1));
    }

    // Best hour
    const hourCounts = {};
    sentLogs.forEach(l => {
      const h = new Date(l.timestamp).getHours();
      hourCounts[h] = (hourCounts[h] || 0) + 1;
    });
    const bestHour = Object.entries(hourCounts).sort(([,a], [,b]) => b - a)[0];

    return { successRate, rcsRate, avgDelay, bestHour: bestHour ? `${bestHour[0].padStart(2, '0')}:00` : '--', processed, sent, total };
  }, [stats, logs]);

  // --- Export handlers ---
  const handleExport = (format) => {
    setShowExportMenu(false);
    const filterParam = exportFilter !== 'all' ? `&status=${exportFilter}` : '';
    window.open(`${API}/campaigns/${campaignId}/report?format=${format}${filterParam}`, "_blank");
  };

  const EXPORT_FILTERS = [
    { value: "all", label: "Todos", icon: "all" },
    { value: "sent", label: "Enviados (RCS)", color: STATUS_COLORS.sent },
    { value: "skipped_no_rcs", label: "Sin RCS (SMS)", color: STATUS_COLORS.skipped_no_rcs },
    { value: "failed", label: "Fallidos", color: STATUS_COLORS.failed },
    { value: "error", label: "Errores", color: STATUS_COLORS.error },
    { value: "pending", label: "Pendientes", color: STATUS_COLORS.pending },
  ];

  if (!stats || stats.total === 0) return null;

  return (
    <div data-testid="campaign-analytics" className="space-y-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-emerald-400" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Tasa de Exito</span>
          </div>
          <p className={`text-3xl font-heading font-bold ${kpis.successRate >= 80 ? 'text-emerald-400' : kpis.successRate >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
            {kpis.successRate}%
          </p>
          <p className="text-[10px] text-zinc-600 mt-1">{kpis.sent} de {kpis.processed} procesados</p>
        </div>
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-blue-400" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Cobertura RCS</span>
          </div>
          <p className="text-3xl font-heading font-bold text-blue-400">{kpis.rcsRate}%</p>
          <p className="text-[10px] text-zinc-600 mt-1">{kpis.total - ((stats.skipped_no_rcs || 0) + (stats.skipped || 0))} con RCS de {kpis.total}</p>
        </div>
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-cyan-400" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Delay Promedio</span>
          </div>
          <p className="text-3xl font-heading font-bold text-cyan-400">
            {kpis.avgDelay > 60 ? `${Math.floor(kpis.avgDelay / 60)}m${kpis.avgDelay % 60}s` : `${kpis.avgDelay}s`}
          </p>
          <p className="text-[10px] text-zinc-600 mt-1">Entre envios exitosos</p>
        </div>
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-purple-400" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Mejor Hora</span>
          </div>
          <p className="text-3xl font-heading font-bold text-purple-400">{kpis.bestHour}</p>
          <p className="text-[10px] text-zinc-600 mt-1">Mayor volumen de envios</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Donut Chart */}
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
          <h4 className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">Distribucion de Estados</h4>
          {donutData.length > 0 ? (
            <div className="flex items-center gap-4">
              <div className="w-32 h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donutData} cx="50%" cy="50%" innerRadius={30} outerRadius={55} paddingAngle={2} dataKey="value" stroke="none">
                      {donutData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-col gap-1.5">
                {donutData.map((entry, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                    <span className="text-[11px] text-zinc-400">{entry.name}</span>
                    <span className="text-[11px] font-mono font-bold text-zinc-300">{entry.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-zinc-600 text-xs text-center py-8">Sin datos</p>
          )}
        </div>

        {/* Hourly Bar Chart */}
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
          <h4 className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">Envios por Hora</h4>
          {hourlyData.length > 0 ? (
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourlyData} barGap={1}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
                  <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 9 }} interval="preserveStartEnd" />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 9 }} width={25} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="sent" name="Enviados" fill="#10B981" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="failed" name="Fallidos" fill="#EF4444" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-zinc-600 text-xs text-center py-8">Sin datos horarios</p>
          )}
        </div>

        {/* Timeline Area Chart */}
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
          <h4 className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">Progreso en el Tiempo</h4>
          {timelineData.length > 1 ? (
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
                  <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 9 }} interval="preserveStartEnd" />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 9 }} width={25} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="sent" name="Enviados" stroke="#10B981" fill="#10B981" fillOpacity={0.15} strokeWidth={2} />
                  <Area type="monotone" dataKey="total" name="Procesados" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.08} strokeWidth={1.5} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-zinc-600 text-xs text-center py-8">Datos insuficientes</p>
          )}
        </div>
      </div>

      {/* Export Row */}
      <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h4 className="text-sm text-zinc-300 font-medium">Exportar Resultados</h4>
            <p className="text-[11px] text-zinc-600 mt-0.5">Descarga el reporte completo o filtrado por estado</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Filter selector */}
            <div className="flex flex-wrap gap-1.5">
              {EXPORT_FILTERS.map(f => (
                <button
                  key={f.value}
                  data-testid={`filter-${f.value}`}
                  onClick={() => setExportFilter(f.value)}
                  className={`px-2.5 py-1.5 text-[11px] rounded-md border transition-all duration-200 ${
                    exportFilter === f.value
                      ? 'bg-white/10 border-white/20 text-white font-medium'
                      : 'bg-transparent border-white/5 text-zinc-500 hover:border-white/10 hover:text-zinc-300'
                  }`}
                >
                  {f.color && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5" style={{ backgroundColor: f.color }} />}
                  {f.label}
                </button>
              ))}
            </div>
            {/* Format dropdown */}
            <div className="relative">
              <button
                data-testid="export-dropdown-btn"
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors duration-200"
              >
                <Download className="w-4 h-4" />
                Exportar
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {showExportMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 z-50 bg-zinc-800 border border-white/10 rounded-lg shadow-2xl overflow-hidden min-w-[180px]">
                    <button
                      data-testid="export-csv"
                      onClick={() => handleExport('csv')}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors text-left"
                    >
                      <FileText className="w-4 h-4 text-emerald-400" />
                      <div>
                        <p className="text-sm text-zinc-200">CSV</p>
                        <p className="text-[10px] text-zinc-500">Separado por comas</p>
                      </div>
                    </button>
                    <button
                      data-testid="export-xlsx"
                      onClick={() => handleExport('xlsx')}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors text-left border-t border-white/5"
                    >
                      <FileSpreadsheet className="w-4 h-4 text-blue-400" />
                      <div>
                        <p className="text-sm text-zinc-200">Excel (XLSX)</p>
                        <p className="text-[10px] text-zinc-500">Con formato y hojas</p>
                      </div>
                    </button>
                    <button
                      data-testid="export-txt"
                      onClick={() => handleExport('txt')}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors text-left border-t border-white/5"
                    >
                      <File className="w-4 h-4 text-zinc-400" />
                      <div>
                        <p className="text-sm text-zinc-200">TXT</p>
                        <p className="text-[10px] text-zinc-500">Texto plano tabulado</p>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
